// Small display helpers. Pure, so they're unit-tested alongside the game logic.

// The podium reads faster as medals than as the numbers 1, 2, 3. Everyone below
// third keeps a plain number, which also keeps ties honest: two players sharing
// rank 3 both show bronze, and nobody is shown a rank they did not earn.
//
// A medal requires a point. Before the first drop reveals, everyone is tied at
// zero and ranked 1, so the leaderboard handed out gold to the entire league on
// day one. Pass the player's points and nobody gets a medal for nothing.
export function rankBadge(rank: number, points?: number): string {
  if (points !== undefined && points <= 0) return String(rank);
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return String(rank);
}

export interface SeasonHighlight {
  label: string;
  name: string;
  detail: string;
}

interface HighlightInput {
  user_id: string;
  name: string;
  points: number;
  correct_count: number;
  attempted_count: number;
  avg_correct_response_ms: number | null;
}

// A season should end with more than one number. These pull a few honest angles
// out of the same standings so more than one person gets named, which is what
// makes a finish feel like a finish instead of a leaderboard that stopped moving.
export function seasonHighlights(board: HighlightInput[]): SeasonHighlight[] {
  const out: SeasonHighlight[] = [];
  if (board.length === 0) return out;

  const played = board.filter((b) => b.attempted_count > 0);
  if (played.length === 0) return out;

  const fastest = played
    .filter((b) => b.avg_correct_response_ms != null && b.correct_count > 0)
    .sort((a, b) => (a.avg_correct_response_ms! - b.avg_correct_response_ms!))[0];
  if (fastest) {
    out.push({
      label: "Quickest trigger",
      name: fastest.name,
      detail: `${(fastest.avg_correct_response_ms! / 1000).toFixed(1)}s average on correct answers`,
    });
  }

  const sharpest = [...played].sort((a, b) => {
    const ra = a.correct_count / a.attempted_count;
    const rb = b.correct_count / b.attempted_count;
    return rb - ra || b.correct_count - a.correct_count;
  })[0];
  if (sharpest && sharpest.attempted_count > 0) {
    const pct = Math.round((sharpest.correct_count / sharpest.attempted_count) * 100);
    out.push({
      label: "Knows everybody",
      name: sharpest.name,
      detail: `${pct}% right across ${sharpest.attempted_count} question${sharpest.attempted_count === 1 ? "" : "s"}`,
    });
  }

  // Only worth naming when somebody actually showed up more than the rest.
  const mostPlayed = [...played].sort((a, b) => b.attempted_count - a.attempted_count)[0];
  const leastPlayed = [...played].sort((a, b) => a.attempted_count - b.attempted_count)[0];
  if (mostPlayed && leastPlayed && mostPlayed.attempted_count > leastPlayed.attempted_count) {
    out.push({
      label: "Never missed a drop",
      name: mostPlayed.name,
      detail: `Answered ${mostPlayed.attempted_count} question${mostPlayed.attempted_count === 1 ? "" : "s"}`,
    });
  }

  return out;
}

// A tight one-liner for the "Recently" list.
//
// The stored reveal_context is written to be read on the results screen, where
// there is room for it. On Home it is one line in a list, and a paragraph there
// buries the next person's update. This shortens for DISPLAY only; the stored
// text is never touched, and the results screen still shows it in full.
//
// The better long-term fix is to have the model write a short summary at
// generation time. This is the display-side stopgap until then.
export function shortenReveal(text: string, maxChars = 90): string {
  const clean = (text ?? "").trim();
  if (clean.length === 0) return "";

  // Prefer the first real sentence. The minimum length keeps an abbreviation
  // like "Mr." or an initial from being mistaken for the end of a thought.
  const MIN_SENTENCE = 30;
  const terminator = /[.!?](\s|$)/g;
  for (const m of clean.matchAll(terminator)) {
    const end = (m.index ?? 0) + 1;
    if (end >= MIN_SENTENCE) {
      const sentence = clean.slice(0, end).trim();
      if (sentence.length <= maxChars) return sentence;
      break;
    }
  }

  if (clean.length <= maxChars) return clean;

  // Clamp on a word boundary. Never cut a word in half.
  const cut = clean.slice(0, maxChars + 1);
  const lastSpace = cut.lastIndexOf(" ");
  const body = (lastSpace > 0 ? cut.slice(0, lastSpace) : clean.slice(0, maxChars)).replace(/[\s,;:.!?-]+$/, "");
  return `${body}…`;
}
